import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ImageModal from './ImageModal';
import propertiesBanner from '../assets/properties-banner.jpg';

const formatCurrency = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

function PropertyList() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // Filter state
  const [city, setCity] = useState('');
  const [minRent, setMinRent] = useState('');
  const [maxRent, setMaxRent] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${process.env.REACT_APP_API_BASE_URL}${url}`;
  };

  const getVerificationBadge = (property) => {
    if (!property) return { label: 'Unverified landlord', tone: 'warning' };
    if (property.landlord_ownership_status === 'approved' || property.landlord_id_status === 'verified') {
      return { label: 'Verified landlord', tone: 'paid' };
    }
    return { label: 'Unverified landlord', tone: 'warning' };
  };

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (city.trim()) params.set('city', city.trim());
      if (minRent) params.set('minRent', minRent);
      if (maxRent) params.set('maxRent', maxRent);
      if (bedrooms) params.set('bedrooms', bedrooms);
      if (propertyType) params.set('propertyType', propertyType);

      const qs = params.toString();
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/properties${qs ? `?${qs}` : ''}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setProperties(data.properties);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [city, minRent, maxRent, bedrooms, propertyType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProperties();
    }, 300); // debounce 300ms to avoid hammering API on every keystroke
    return () => clearTimeout(timer);
  }, [fetchProperties]);

  const clearFilters = () => {
    setCity('');
    setMinRent('');
    setMaxRent('');
    setBedrooms('');
    setPropertyType('');
  };

  const hasActiveFilters = city || minRent || maxRent || bedrooms || propertyType;

  return (
    <main className="App-main">
      <section className="dashboard-section" style={{ marginBottom: '1.5rem' }}>
        <div className="properties-banner">
          <img src={propertiesBanner} alt="Modern rental community" />
        </div>
        <p className="eyebrow-tag">Powered by rentual.co</p>
        <h2>Available Properties</h2>
        <p className="status-text">
          Listings are managed by rentual.co with renewal-ready leases and secure online applications.
        </p>
      </section>

      {/* Search & Filter Bar */}
      <div className="dashboard-section" style={{ marginBottom: '1rem', padding: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search by city…"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="form-control"
            style={{ flex: '1 1 200px', minWidth: '150px' }}
          />
          <button
            className="secondary-button"
            onClick={() => setShowFilters(!showFilters)}
            style={{ whiteSpace: 'nowrap' }}
          >
            {showFilters ? 'Hide Filters' : 'Filters'}
            {hasActiveFilters && <span style={{ marginLeft: '0.35rem', color: '#2f6fed' }}>●</span>}
          </button>
          {hasActiveFilters && (
            <button className="secondary-button" onClick={clearFilters} style={{ whiteSpace: 'nowrap', color: '#999' }}>
              Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
            <div style={{ flex: '1 1 120px' }}>
              <label className="status-text" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem' }}>Min Rent</label>
              <input
                type="number"
                placeholder="$0"
                value={minRent}
                onChange={(e) => setMinRent(e.target.value)}
                className="form-control"
                min="0"
              />
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <label className="status-text" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem' }}>Max Rent</label>
              <input
                type="number"
                placeholder="Any"
                value={maxRent}
                onChange={(e) => setMaxRent(e.target.value)}
                className="form-control"
                min="0"
              />
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <label className="status-text" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem' }}>Bedrooms</label>
              <select value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} className="form-control">
                <option value="">Any</option>
                <option value="0">Studio</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4+</option>
              </select>
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <label className="status-text" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem' }}>Type</label>
              <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)} className="form-control">
                <option value="">Any</option>
                <option value="apartment">Apartment</option>
                <option value="house">House</option>
                <option value="condo">Condo</option>
                <option value="townhouse">Townhouse</option>
                <option value="duplex">Duplex</option>
                <option value="basement">Basement</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <p className="status-text">Loading properties…</p>
      ) : error ? (
        <p className="error-message">Error: {error}</p>
      ) : (
        <>
          {hasActiveFilters && (
            <p className="status-text" style={{ marginBottom: '0.75rem' }}>
              {properties.length} {properties.length === 1 ? 'property' : 'properties'} found
              {city ? ` in "${city}"` : ''}
            </p>
          )}

          <div className="property-grid">
            {properties.length > 0 ? (
              properties.map((property) => (
                <div key={property.id} className="property-card">
                  {property.photo_urls && property.photo_urls.length > 0 && (
                    <div>
                      <img
                        src={resolveImageUrl(property.photo_urls[0])}
                        alt={property.address}
                        className="property-image"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedImage(resolveImageUrl(property.photo_urls[0]));
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                      <div className="property-thumb-row">
                        {property.photo_urls.slice(1, 5).map((url, idx) => (
                          <img
                            key={idx}
                            src={resolveImageUrl(url)}
                            alt={`${property.address} thumbnail ${idx + 2}`}
                            className="property-thumb-small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedImage(resolveImageUrl(url));
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="property-info">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <h3 style={{ marginBottom: 0 }}><Link to={`/properties/${property.id}`}>{property.address}{property.unit ? `, Unit ${property.unit}` : ''}, {property.city}</Link></h3>
                      <Link to="/verification" className={`status-pill ${getVerificationBadge(property).tone}`} title="Learn what landlord verification means">
                        {getVerificationBadge(property).label}
                      </Link>
                    </div>
                    <p className="property-rent">{formatCurrency(property.rent)}/month</p>
                    <div className="property-specs">
                      <span>{property.bedrooms} Bed</span>
                      <span>{property.bathrooms} Bath</span>
                      <span>{property.property_type}</span>
                    </div>
                    <div className="property-actions">
                      <Link to={`/properties/${property.id}`} className="primary-button view-details-button">View Details</Link>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="dashboard-section" style={{ textAlign: 'center', padding: '2rem' }}>
                <p style={{ fontSize: '1.1rem', fontWeight: '600' }}>No properties found</p>
                <p className="status-text">
                  {hasActiveFilters ? 'Try adjusting your filters or search for a different city.' : 'No properties available at the moment. Check back soon!'}
                </p>
                {hasActiveFilters && (
                  <button className="secondary-button" onClick={clearFilters} style={{ marginTop: '0.75rem' }}>
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}
      <ImageModal imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />
    </main>
  );
}

export default PropertyList;
